/* eslint-disable @typescript-eslint/no-explicit-any */
import { decryptBy3DES, H5_3DES_KEY } from '../../utils/crypto';
import {
  AmountSummaryVo,
  CheckoutRootVo,
  FloorFragmentVo,
  IPaymentVo,
  ITimeVo,
  LoopObjectVo,
  PaymentVo,
  PickupUserVo,
  PointInfoVo,
  PointVo,
  PreSaleDefaultVo,
  PreSaleDepositVo,
  PreSaleMobileVo,
  ProductVo,
  RootObjectVo,
  ShipmentVo,
  StructureVo,
  SummaryItemVo,
  VenderVo,
  ZeroInfoVo,
} from './checkout.interface';
import { CHECKOUT_FLOOR_TYPE } from './constants/checkoutType';
import { AMOUNT_FLOOR_TYPE, DELIVERY_TYPE, STOCK_STATUS_CODE } from './constants/constants';
import { floors2Fragment } from './convert/CommonDto2Vo';
import { getFormatPaymentList } from './convert/PaymentDto2Vo';

/**
 * 判断当前楼层
 * @param floorFragment 数据片段
 * @param uuid 楼层唯一标识
 * @param namespace 楼层名称
 */
function definedFloor(floorFragment: FloorFragmentVo, uuid: string, namespace: string) {
  return (
    floorFragment[uuid] &&
    floorFragment[uuid].type &&
    floorFragment[uuid].type.namespace === namespace
  );
}

/**
 * 将返回楼层转化为可配置楼层
 * @param floorObject 楼层结构
 * @param structure 维护楼层结构表
 */
function unfoldRootObject(floorObject: RootObjectVo<any>, structure: any) {
  const obj: LoopObjectVo = {};
  Object.keys(floorObject.info || {}).forEach((key: string) => {
    Object.keys(structure[key] || {}).forEach((useKsy: string) => {
      obj[structure[key][useKsy]] = floorObject.info[key][useKsy] ?? '';
    });
  });
  return obj;
}

/**
 * 获取销售属性
 * @param floorObject
 * @param structure
 */
function getPartList(floorObject: RootObjectVo<any>, structure?: any): string[] {
  const array: string[] = [];
  floorObject.partList?.forEach((part) => {
    const attr = unfoldRootObject(part, CHECKOUT_FLOOR_TYPE.PRODUCT_FLOOR.PART_LIST_STRUCTURE);
    if (attr.saleValue) {
      array.push(attr.saleValue);
    }
  });
  return array;
}

/**
 * 获取公共的属性
 * @param floorObject
 * @param structure
 */
function getCommonPartList(floorObject: RootObjectVo<any>, structure: any): any[] {
  const array: any[] = [];
  floorObject.partList?.forEach((part) => {
    const attr = unfoldRootObject(part, structure);
    array.push({ ...attr });
  });
  return array;
}

/**
 * 通用方法：根据父楼层uuid，获取其所有关联的指定类型子楼层，并展平成业务对象数组
 * @param data 结构化数据（含 floors、structure.hierarchy）
 * @param parentUuid 父节点uuid（如商品floor uuid）
 * @param childNamespace 子节点namespace（如服务floor、属性floor等）
 * @param childStructure unfoldRootObject的结构定义（解析子节点用）
 * @returns 子节点展平对象数组
 */
function getChildFloorListByParent(
  data: any,
  parentUuid: string,
  childNamespace: string,
  childStructure: any,
): any[] {
  if (
    !data ||
    !data.floors ||
    !data.structure ||
    !data.structure.hierarchy ||
    !parentUuid ||
    !childStructure
  ) {
    return [];
  }
  const childUuids = data.structure.hierarchy[parentUuid] || [];
  const childFloors = data.floors.filter(
    (floor: any) => childUuids.includes(floor.uuid) && floor?.type?.namespace === childNamespace,
  );
  return childFloors.map((floor: any) => unfoldRootObject(floor, childStructure));
}

/**
 * 将领域产品返回转化为传统楼层结构
 * @param floors
 * @param structure
 */
function getFormatCheckoutInfo(floors: RootObjectVo<any>[], structure: StructureVo) {
  // 最终转化结构
  let checkoutObject: CheckoutRootVo = {
    couponInfo: {},
    userAddress: {},
    // amount: {},
    payment: [], // payment List
    venderList: [],
    imageDomain: '',
    amountSummary: {} as AmountSummaryVo,
    pointInfo: {
      pointList: [],
    },
    selectSelfPick: false,
    outStockProductList: [],
    noStockTitle: '', // 无货标题
    stockStatus: 0,
    presaleDefault: null,
    presaleMobile: null,
    presaleDeposit: null,
    zeroInfo: {},
  };

  if (!floors || !floors.length || !structure) {
    return checkoutObject;
  }
  // 唯一根节点
  const root = structure.root;
  // 结构
  const hierarchy = structure.hierarchy;

  // floors转floorMap 方便取值
  const floorFragment: FloorFragmentVo = floors2Fragment(floors);

  hierarchy[root].map((uuid: string) => {
    // 地址
    if (
      definedFloor(floorFragment, uuid, CHECKOUT_FLOOR_TYPE.ADDRESS_FLOOR.ADDRESS_FLOOR_NAMESPACE)
    ) {
      const address = unfoldRootObject(
        floorFragment[uuid],
        CHECKOUT_FLOOR_TYPE.ADDRESS_FLOOR.STRUCTURE,
      );
      // 解密用户名称
      const name = decryptBy3DES(address?.encryptedName, H5_3DES_KEY);
      // 解密用户详细地址
      const addressDetail = decryptBy3DES(address?.encryptedAddressDetail, H5_3DES_KEY);
      // 解密用户完整地址
      const fullAddress = decryptBy3DES(address?.encryptedFullAddress, H5_3DES_KEY);
      checkoutObject.userAddress = {
        ...address,
        provinceId: address.areaIdMap.idProvince || '',
        provinceName: address.areaNameMap.provinceName,
        cityId: address.areaIdMap.idCity || '',
        cityName: address.areaNameMap.cityName,
        districtId: address.areaIdMap.idArea || '',
        districtName: address.areaNameMap.areaName,
        townId: address.areaIdMap.iVown || '',
        townName: address.areaNameMap.townName,
        name: name || '',
        addressDetail: addressDetail,
        fullAddress: fullAddress,
      };
    }
    // 支付楼层
    if (
      definedFloor(floorFragment, uuid, CHECKOUT_FLOOR_TYPE.PAYMENT_FLOOR.PAYMENT_FLOOR_NAMESPACE)
    ) {
      const payment = unfoldRootObject(
        floorFragment[uuid],
        CHECKOUT_FLOOR_TYPE.PAYMENT_FLOOR.STRUCTURE,
      ) as PaymentVo;
      console.log('payment', floorFragment[uuid], payment);

      const paymentList = getFormatPaymentList(
        getCommonPartList(
          floorFragment[uuid],
          CHECKOUT_FLOOR_TYPE.PAYMENT_FLOOR.PART_LIST_STRUCTURE,
        ),
      );

      checkoutObject.payment = paymentList as IPaymentVo[];
    }
    // 支付金额
    if (
      definedFloor(floorFragment, uuid, CHECKOUT_FLOOR_TYPE.AMOUNT_FLOOR.AMOUNT_FLOOR_NAMESPACE)
    ) {
      const orderTotal = unfoldRootObject(
        floorFragment[uuid],
        CHECKOUT_FLOOR_TYPE.AMOUNT_FLOOR.STRUCTURE,
      );
      checkoutObject.zeroInfo = {
        isZeroOrder: orderTotal.isZeroOrder,
        zeroOrderText: orderTotal.zeroOrderText, // "0元订单显示文案"
      } as ZeroInfoVo;
    }
    // 商品金额 立减
    if (
      definedFloor(
        floorFragment,
        uuid,
        CHECKOUT_FLOOR_TYPE.PRODUCT_AMOUNT_FLOOR.PRODUCT_AMOUNT_FLOOR_NAMESPACE,
      )
    ) {
      const obj = unfoldRootObject(
        floorFragment[uuid],
        CHECKOUT_FLOOR_TYPE.PRODUCT_AMOUNT_FLOOR.STRUCTURE,
      );

      try {
        obj.amountDetail = JSON.parse(obj.amountDetail);
      } catch (error) {
        delete obj.amountDetail;
      }
      // 商品金额-Item(s) Total--0
      if (obj.floorType === AMOUNT_FLOOR_TYPE.PRODUCT_AMOUNT) {
        (checkoutObject.amountSummary as AmountSummaryVo).productAmount = obj as SummaryItemVo;
      }
      // 纯促销金额，不包含券/码金额--1
      if (obj.floorType === AMOUNT_FLOOR_TYPE.PROMOTIONS) {
        (checkoutObject.amountSummary as AmountSummaryVo).promotions = obj as SummaryItemVo;
      }
      // 促销小计--Promotions--4 包含促销、券、码金额 暂不使用
      if (obj.floorType === AMOUNT_FLOOR_TYPE.PROMOTION_SUB_TOTAL) {
        (checkoutObject.amountSummary as any).promotionsTotal = obj as SummaryItemVo;
      }

      console.log(' ROOT  ', obj);
      // 订单总金额--应付金额--Order Total--2
      if (obj.floorType === AMOUNT_FLOOR_TYPE.TOTAL) {
        (checkoutObject.amountSummary as AmountSummaryVo).totalAmount = {
          label: obj.label,
          subLabel: obj.subLabel,
          amount: obj.factAmount,
          floorType: obj.floorType,
        } as SummaryItemVo;
      }
      // 优惠券金额--Coupon--5
      if (obj.floorType === AMOUNT_FLOOR_TYPE.COUPONS) {
        (checkoutObject.amountSummary as AmountSummaryVo).coupons = obj as SummaryItemVo;
      }
      // 优惠券金额--Discount--6
      if (obj.floorType === AMOUNT_FLOOR_TYPE.DISCOUNT) {
        (checkoutObject.amountSummary as AmountSummaryVo).discount = obj as SummaryItemVo;
      }
      // 小计--Subtotal--9
      if (obj.floorType === AMOUNT_FLOOR_TYPE.SUBTOTAL) {
        (checkoutObject.amountSummary as AmountSummaryVo).subtotal = obj as SummaryItemVo;
      }
      // 折扣--Discounts--10
      if (obj.floorType === AMOUNT_FLOOR_TYPE.DISCOUNTS) {
        (checkoutObject.amountSummary as AmountSummaryVo).discounts = obj as SummaryItemVo;
      }
    }
    // 商品库存状态楼层
    if (
      definedFloor(
        floorFragment,
        uuid,
        CHECKOUT_FLOOR_TYPE.PRODUCT_STOCK_FLOOR.PRODUCT_STOCK_NAMESPACE,
      )
    ) {
      checkoutObject = Object.assign(
        checkoutObject,
        unfoldRootObject(floorFragment[uuid], CHECKOUT_FLOOR_TYPE.PRODUCT_STOCK_FLOOR.STRUCTURE),
      );
    }
    // 运费金额
    if (
      definedFloor(
        floorFragment,
        uuid,
        CHECKOUT_FLOOR_TYPE.FREIGHT_AMOUNT_FLOOR.FREIGHT_AMOUNT_FLOOR_NAMESPACE,
      )
    ) {
      const obj = unfoldRootObject(
        floorFragment[uuid],
        CHECKOUT_FLOOR_TYPE.FREIGHT_AMOUNT_FLOOR.STRUCTURE,
      );

      checkoutObject.amountSummary = Object.assign(checkoutObject.amountSummary, {
        freightAmount: obj,
      });
    }
    // 优惠券信息
    if (
      definedFloor(floorFragment, uuid, CHECKOUT_FLOOR_TYPE.COUPON_FLOOR.COUPON_FLOOR_NAMESPACE)
    ) {
      checkoutObject.couponInfo = unfoldRootObject(
        floorFragment[uuid],
        CHECKOUT_FLOOR_TYPE.COUPON_FLOOR.STRUCTURE,
      );
    }
    // 服务费信息楼层
    if (
      definedFloor(
        floorFragment,
        uuid,
        CHECKOUT_FLOOR_TYPE.TOTAL_SERVICE_FEE_FLOOR.TOTAL_SERVICE_FEE_NAMESPACE,
      )
    ) {
      const obj = unfoldRootObject(
        floorFragment[uuid],
        CHECKOUT_FLOOR_TYPE.TOTAL_SERVICE_FEE_FLOOR.STRUCTURE,
      );
      checkoutObject.amountSummary = Object.assign(checkoutObject.amountSummary, {
        totalService: {
          ...obj,
          packingFeeSubTip: obj.packingFeeSubTip,
          tip: obj.iconFlag,
          floorType: '', //
        },
      });
    }
    // 积分
    if (definedFloor(floorFragment, uuid, CHECKOUT_FLOOR_TYPE.POINT_FLOOR.POINT_FLOOR_NAMESPACE)) {
      checkoutObject.pointInfo = {
        ...unfoldRootObject(floorFragment[uuid], CHECKOUT_FLOOR_TYPE.POINT_FLOOR.STRUCTURE),
        pointList: [],
      };
      // 获取积分列表
      floorFragment[uuid]?.partList?.map((item: RootObjectVo<any>) => {
        if (
          item &&
          item.type &&
          item.type.namespace === CHECKOUT_FLOOR_TYPE.POINT_FLOOR.PART_LIST_NAMESPACE
        ) {
          (checkoutObject.pointInfo as PointInfoVo).pointList.push(
            unfoldRootObject(item, CHECKOUT_FLOOR_TYPE.POINT_FLOOR.PART_LIST_STRUCTURE) as PointVo,
          );
        }
      });
    }
    // 店铺
    if (
      definedFloor(floorFragment, uuid, CHECKOUT_FLOOR_TYPE.VENDER_FLOOR.VENDER_FLOOR_NAMESPACE)
    ) {
      const vender: VenderVo = {
        venderName: '',
        venderId: '',
        nodeList: [],
        shipment: {},
        promise: {},
        remark: {},
        xnzt: [],
        selfPickSitePart: {},
        ...unfoldRootObject(floorFragment[uuid], CHECKOUT_FLOOR_TYPE.VENDER_FLOOR.STRUCTURE),
      };
      const xnzt: any[] = [];
      // 店铺下商品、配送
      (hierarchy[uuid] || []).map((uuid: string) => {
        // 配送
        if (
          definedFloor(
            floorFragment,
            uuid,
            CHECKOUT_FLOOR_TYPE.SHIPMENT_FLOOR.SHIPMENT_FLOOR_NAMESPACE,
          )
        ) {
          vender.shipment = unfoldRootObject(
            floorFragment[uuid],
            CHECKOUT_FLOOR_TYPE.SHIPMENT_FLOOR.STRUCTURE,
          );
          if ((vender.shipment as ShipmentVo).shipmentId === DELIVERY_TYPE.PICK) {
            checkoutObject.selectSelfPick = true;
          }
          // 自提点信息
          floorFragment[uuid]?.partList?.map((item: RootObjectVo<any>) => {
            if (
              item &&
              item.type &&
              item.type.namespace === CHECKOUT_FLOOR_TYPE.SHIPMENT_FLOOR.SENCOND_PART_LIST_NAMESPACE
            ) {
              vender.selfPickSitePart = unfoldRootObject(
                item,
                CHECKOUT_FLOOR_TYPE.SHIPMENT_FLOOR.PART_LIST_STRUCTURE,
              );
            }
          });
        }
        // 备注
        if (
          definedFloor(floorFragment, uuid, CHECKOUT_FLOOR_TYPE.REMARK_FLOOR.REMARK_FLOOR_NAMESPACE)
        ) {
          vender.remark = unfoldRootObject(
            floorFragment[uuid],
            CHECKOUT_FLOOR_TYPE.REMARK_FLOOR.STRUCTURE,
          );
        }
        // 商品
        if (
          definedFloor(
            floorFragment,
            uuid,
            CHECKOUT_FLOOR_TYPE.PRODUCT_FLOOR.PRODUCT_FLOOR_NAMESPACE,
          )
        ) {
          const product = unfoldRootObject(
            floorFragment[uuid],
            CHECKOUT_FLOOR_TYPE.PRODUCT_FLOOR.STRUCTURE,
          ) as ProductVo;
          vender.nodeList.push({
            ...product,
            attrList: getPartList(
              floorFragment[uuid],
              CHECKOUT_FLOOR_TYPE.PRODUCT_FLOOR.PART_LIST_STRUCTURE,
            ),
            serviceProductList: getChildFloorListByParent(
              { floors, structure },
              uuid,
              CHECKOUT_FLOOR_TYPE.SERVICE_PRODUCT_FLOOR.SERVICE_PRODUCT_FLOOR_NAMESPACE,
              CHECKOUT_FLOOR_TYPE.SERVICE_PRODUCT_FLOOR.STRUCTURE,
            ),
          });
          if (product.stockStatusCode === STOCK_STATUS_CODE.OUT_STOCK) {
            checkoutObject.outStockProductList.push(product);
          }
        }

        // 快递运输时效 在楼层外初始化，兼容后端没返回运费时效楼层的问题
        (vender.promise as any) = {
          promiseList: [],
        };

        // 运输promise
        if (
          definedFloor(
            floorFragment,
            uuid,
            CHECKOUT_FLOOR_TYPE.PROMISE_FLOOR.PROMISE_FLOOR_NAMESPACE,
          )
        ) {
          // 是否支持门店自提字段
          vender.promise = unfoldRootObject(
            floorFragment[uuid],
            CHECKOUT_FLOOR_TYPE.PROMISE_FLOOR.STRUCTURE,
          );
          // 快递运输时效
          (vender.promise as any).promiseList = [];
          floorFragment[uuid]?.partList?.map((item: RootObjectVo<any>) => {
            if (
              item &&
              item.type &&
              item.type.namespace === CHECKOUT_FLOOR_TYPE.PROMISE_FLOOR.PART_LIST_NAMESPACE
            ) {
              let timeArr: ITimeVo[] = [];
              item.partList?.map((v: RootObjectVo<any>) => {
                if (
                  v &&
                  v.type &&
                  v.type.namespace === CHECKOUT_FLOOR_TYPE.PROMISE_FLOOR.SENCOND_PART_LIST_NAMESPACE
                ) {
                  const tmp: ITimeVo = unfoldRootObject(
                    v,
                    CHECKOUT_FLOOR_TYPE.PROMISE_FLOOR.SENCOND_PART_LIST_STRUCTURE,
                  ) as ITimeVo;
                  timeArr.push(tmp);
                }
              });

              (vender.promise as any).promiseList.push({
                ...unfoldRootObject(item, CHECKOUT_FLOOR_TYPE.PROMISE_FLOOR.PART_LIST_STRUCTURE),
                timeList: timeArr,
              });
            }
          });
        }
        // 自提promise
        if (
          definedFloor(floorFragment, uuid, CHECKOUT_FLOOR_TYPE.PICK_FLOOR.PICK_FLOOR_NAMESPACE)
        ) {
          vender.promise = unfoldRootObject(
            floorFragment[uuid],
            CHECKOUT_FLOOR_TYPE.PICK_FLOOR.STRUCTURE,
          );
        }
        // 促销加价购 多包装一层 从多包装的一层中取数据
        if (uuid.includes('totalPriceProductFloor') && hierarchy[uuid]) {
          (hierarchy[uuid] || []).map((uid: string) => {
            let product;
            if (
              definedFloor(
                floorFragment,
                uid,
                CHECKOUT_FLOOR_TYPE.PRODUCT_FLOOR.PRODUCT_FLOOR_NAMESPACE,
              )
            ) {
              product = unfoldRootObject(
                floorFragment[uid],
                CHECKOUT_FLOOR_TYPE.PRODUCT_FLOOR.STRUCTURE,
              ) as ProductVo;
            } else if (
              definedFloor(
                floorFragment,
                uid,
                CHECKOUT_FLOOR_TYPE.ADDITIONAL_PRODUCT_FLOOR.ADDITIONAL_PRODUCT_FLOOR_NAMESPACE,
              )
            ) {
              product = unfoldRootObject(
                floorFragment[uid],
                CHECKOUT_FLOOR_TYPE.ADDITIONAL_PRODUCT_FLOOR.STRUCTURE,
              ) as ProductVo;
            }
            vender.nodeList.push({
              ...product,
              attrList: getPartList(
                floorFragment[uid],
                CHECKOUT_FLOOR_TYPE.PRODUCT_FLOOR.PART_LIST_STRUCTURE,
              ),
            });
            if (product?.stockStatusCode === STOCK_STATUS_CODE.OUT_STOCK) {
              checkoutObject.outStockProductList.push(product);
            }
          });
        }

        // 促销 虚拟组套 实商品挂在虚商品下
        if (uuid.includes('virtualProductFloor') && hierarchy[uuid]) {
          // 虚拟组套虚套组
          let virtualProduct: any = {};
          // 虚拟组套实商品列表
          const retailProductList: any[] = [];
          const virtualProductFloor = unfoldRootObject(
            floorFragment[uuid],
            CHECKOUT_FLOOR_TYPE.VIRTUAL_PRODUCT_FLOOR.STRUCTURE,
          ) as any;
          (hierarchy[uuid] || []).map((uid: string) => {
            let product;
            if (
              definedFloor(
                floorFragment,
                uid,
                CHECKOUT_FLOOR_TYPE.VIRTUAL_COMMODITY_FLOOR.VIRTUAL_COMMODITY_FLOOR_NAMESPACE,
              )
            ) {
              // 虚拟组套虚套组
              virtualProduct = unfoldRootObject(
                floorFragment[uid],
                CHECKOUT_FLOOR_TYPE.VIRTUAL_COMMODITY_FLOOR.STRUCTURE,
              ) as ProductVo;
              // 虚拟组套实商品数量
              virtualProduct.num = virtualProductFloor.num;
              // 虚拟组套总数
              virtualProduct.suitNum = virtualProductFloor.suitNum;
            } else if (
              definedFloor(
                floorFragment,
                uid,
                CHECKOUT_FLOOR_TYPE.PRODUCT_FLOOR.PRODUCT_FLOOR_NAMESPACE,
              )
            ) {
              // 实商品
              product = unfoldRootObject(
                floorFragment[uid],
                CHECKOUT_FLOOR_TYPE.PRODUCT_FLOOR.STRUCTURE,
              ) as ProductVo;
              retailProductList.push({
                ...product,
                attrList: getPartList(
                  floorFragment[uid],
                  CHECKOUT_FLOOR_TYPE.PRODUCT_FLOOR.PART_LIST_STRUCTURE,
                ),
              });
            }
            if (product?.stockStatusCode === STOCK_STATUS_CODE.OUT_STOCK) {
              checkoutObject.outStockProductList.push(product);
            }
          });
          virtualProduct.virtualList = retailProductList;
          xnzt.push({ ...virtualProduct });
        }
        // 同一店铺所有虚拟组套放在一起
        vender.xnzt = xnzt;
      });
      checkoutObject.venderList.push(vender);
    }
    // 预售楼层
    if (
      definedFloor(
        floorFragment,
        uuid,
        CHECKOUT_FLOOR_TYPE.PRESALE_DEFAULT_FLOOR.PRESALE_DEFAULT_NAMESPACE,
      )
    ) {
      checkoutObject.presaleDefault = {
        ...unfoldRootObject(
          floorFragment[uuid],
          CHECKOUT_FLOOR_TYPE.PRESALE_DEFAULT_FLOOR.STRUCTURE,
        ),
      } as PreSaleDefaultVo;
    }
    if (
      definedFloor(
        floorFragment,
        uuid,
        CHECKOUT_FLOOR_TYPE.PRESALE_DEPOSIT_FLOOR.PRESALE_DEPOSIT_NAMESPACE,
      )
    ) {
      checkoutObject.presaleDeposit = {
        ...unfoldRootObject(
          floorFragment[uuid],
          CHECKOUT_FLOOR_TYPE.PRESALE_DEPOSIT_FLOOR.STRUCTURE,
        ),
      } as PreSaleDepositVo;
    }
    if (
      definedFloor(
        floorFragment,
        uuid,
        CHECKOUT_FLOOR_TYPE.PRESALE_MOBILE_FLOOR.PRESALE_MOBILE_NAMESPACE,
      )
    ) {
      checkoutObject.presaleMobile = {
        ...unfoldRootObject(
          floorFragment[uuid],
          CHECKOUT_FLOOR_TYPE.PRESALE_MOBILE_FLOOR.STRUCTURE,
        ),
      } as PreSaleMobileVo;
    }

    // 自提联系人信息
    if (
      definedFloor(floorFragment, uuid, CHECKOUT_FLOOR_TYPE.PICKUP_USER_FLOOR.PICK_USER_NAMESPACE)
    ) {
      checkoutObject.pickUser = {
        ...unfoldRootObject(floorFragment[uuid], CHECKOUT_FLOOR_TYPE.PICKUP_USER_FLOOR.STRUCTURE),
      } as PickupUserVo;
    }
  });

  return checkoutObject;
}

export { getFormatCheckoutInfo };
